import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { searchFlights } from 'api/flights'
import { addToCheckoutCart } from 'actions/CheckoutActions'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { isLeFlightSavedItemData } from 'tripPlanner/api/types'
import type { ModalContextType } from 'contexts/ModalContext'
import { reportLog } from 'services/userMonitoring'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { parseSearchParams } from './flightShortlistUtils'
import { getCachedJourney } from './shortlistedJourneyCache'
import { prepareSelectedFlightCheckoutItem } from './prepareFlightCheckoutItem'
import { findShortlistedFlightJourneys } from './shortlistedFlightJourneyMatching'
import type { BookableFlightOption, BookFlightResult } from 'tripPlanner/components/Chat/Flights/flightRecommendationBookingTypes'

interface UseBookResult {
  bookFlight: (option: BookableFlightOption) => Promise<BookFlightResult>
  bookingItemId: number | undefined
  searchingItemId: number | undefined
}

function isBookableCachedJourney(entry?: { journey: App.JourneyV2; searchId?: string }): boolean {
  if (!entry?.searchId) return false

  const firstFlight = entry.journey.flightGroup?.flights?.[0]
  return !!(
    entry.journey.price?.all?.totalFare != null
    && firstFlight?.marketingAirline?.code
  )
}

function useBookShortlistedFlight(showModal: ModalContextType): UseBookResult {
  const [bookingItemId, setBookingItemId] = useState<number | undefined>(undefined)
  const [searchingItemId, setSearchingItemId] = useState<number | undefined>(undefined)
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const email = useAppSelector(state => state.auth.account.email)
  const canRedeemLuxPlusBenefits = useAppSelector(getCanRedeemLuxPlusBenefits)
  const canRequestLuxPlusFlightPricing = canRedeemLuxPlusBenefits && region === 'AU'

  const bookFlight = useCallback(async(option: BookableFlightOption): Promise<BookFlightResult> => {
    if (!isLeFlightSavedItemData(option.savedItemData)) return 'error'

    const data = option.savedItemData
    const params = parseSearchParams(data.searchParams)
    if (!params) {
      reportLog('[TripChat] Flight recommendation booking missing search params', {
        itemId: option.itemId,
        hasJourneyKey: !!data.journeyKey,
        hasSearchId: !!data.searchId,
      }, 'warn')
      showSnackbar(intl.formatMessage({
        id: 'R6wd52-oZj65JhpjjL7G',
        defaultMessage: 'Unable to book this flight. Search parameters are missing.',
        description: 'Error snackbar shown when a shortlisted flight is missing search parameters needed for booking',
      }), 'critical')
      return 'missing_search_params'
    }

    setBookingItemId(option.itemId)

    try {
      const returnLeg = option.returnLeg
      const returnLegData = isLeFlightSavedItemData(returnLeg?.savedItemData) ? returnLeg.savedItemData : undefined
      const isReturn = !!returnLeg && !!returnLegData
      const fareType: 'return' | 'oneWay' = params.fareType === 'return' ? 'return' : 'oneWay'

      const flightUnavailableMessage = intl.formatMessage({
        id: 'hqp07ZRkaLAescD38LIo',
        defaultMessage: 'This flight is no longer available. Please search for new flights.',
        description: 'Warning snackbar shown when a shortlisted flight cannot be found in current search results',
      })

      let matchedDepartingJourney: App.JourneyV2 | undefined
      let matchedReturningJourney: App.JourneyV2 | undefined
      let searchId: string | undefined
      let sessionId: string | undefined
      let searchExpiry: number

      const departingCached = data.journeyKey ? getCachedJourney(data.journeyKey, canRequestLuxPlusFlightPricing) : undefined
      const returningCached = returnLegData?.journeyKey ? getCachedJourney(returnLegData.journeyKey, canRequestLuxPlusFlightPricing) : undefined

      const departingCacheValid = !!departingCached && isBookableCachedJourney(departingCached)
      const returningCacheValid = !isReturn || (!!returningCached && isBookableCachedJourney(returningCached))

      if (departingCached && departingCacheValid && returningCacheValid) {
        matchedDepartingJourney = departingCached.journey
        matchedReturningJourney = returningCached?.journey
        searchId = departingCached.searchId
        sessionId = departingCached.sessionId
        searchExpiry = departingCached.cachedAt + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000
      } else {
        setSearchingItemId(option.itemId)

        const searchResult = await searchFlights({
          itinerary: params.itinerary,
          currency,
          occupancies: params.occupancies,
          fareClass: params.fareClass,
          region,
          fareType: params.fareType,
          email,
        }, {
          enableSabreContent: true,
          ...(canRequestLuxPlusFlightPricing ? { requestLuxPlusPricing: true } : {}),
        })

        setSearchingItemId(undefined)
        searchId = searchResult.searchId
        sessionId = searchResult.sessionId
        searchExpiry = Date.now() + FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60 * 1000

        if (isReturn) {
          const departingFares = searchResult.onewayFares[0] ?? []
          const returningFares = searchResult.onewayFares[1] ?? []

          const matchedJourneys = findShortlistedFlightJourneys({
            onewayFares: searchResult.onewayFares,
            departingJourneyKey: data.journeyKey,
            returningJourneyKey: returnLegData.journeyKey,
          })
          matchedDepartingJourney = matchedJourneys.departingJourney
          matchedReturningJourney = matchedJourneys.returningJourney

          if (!matchedDepartingJourney || !matchedReturningJourney) {
            reportLog('[TripChat] Return flight recommendation unavailable after refresh', {
              itemId: option.itemId,
              fareType,
              hasDepartingJourneyKey: !!data.journeyKey,
              hasReturningJourneyKey: !!returnLegData.journeyKey,
              matchedDeparting: !!matchedDepartingJourney,
              matchedReturning: !!matchedReturningJourney,
              departingFareCount: departingFares.length,
              returningFareCount: returningFares.length,
            }, 'warn')
            showSnackbar(flightUnavailableMessage, 'warning')
            return 'unavailable'
          }
        } else {
          const allFares = searchResult.onewayFares.flat()
          const matchedJourneys = findShortlistedFlightJourneys({
            onewayFares: searchResult.onewayFares,
            departingJourneyKey: data.journeyKey,
          })
          matchedDepartingJourney = matchedJourneys.departingJourney

          if (!matchedDepartingJourney) {
            reportLog('[TripChat] One-way flight recommendation unavailable after refresh', {
              itemId: option.itemId,
              fareType,
              hasJourneyKey: !!data.journeyKey,
              fareCount: allFares.length,
            }, 'warn')
            showSnackbar(flightUnavailableMessage, 'warning')
            return 'unavailable'
          }
        }
      }

      if (!searchId) {
        reportLog('[TripChat] Flight recommendation booking missing search id', {
          itemId: option.itemId,
          fareType,
          hasDepartingJourney: !!matchedDepartingJourney,
          hasReturningJourney: !!matchedReturningJourney,
        }, 'warn')
        showSnackbar(flightUnavailableMessage, 'warning')
        return 'unavailable'
      }

      const preparedResult = await prepareSelectedFlightCheckoutItem({
        departingJourney: matchedDepartingJourney,
        returningJourney: matchedReturningJourney,
        searchParams: params,
        searchId,
        sessionId,
        searchExpiry,
        showModal,
      })

      if (preparedResult.status === 'cancelled') return 'cancelled'
      if (preparedResult.status !== 'success') return 'error'

      dispatch(addToCheckoutCart([preparedResult.prepared.checkoutItem]))
      dispatch(navigateToCheckout())
      return 'success'
    } catch(error) {
      reportLog('[TripChat] Flight recommendation booking failed', {
        itemId: option.itemId,
        error: error instanceof Error ? error.message : 'Unknown error',
        hasJourneyKey: !!data.journeyKey,
        hasSearchId: !!data.searchId,
      }, 'error')
      showSnackbar(intl.formatMessage({
        id: 'Xi8uEjkdq0mQ8_7rn8QW',
        defaultMessage: 'Something went wrong while preparing your booking. Please try again.',
        description: 'Error snackbar shown when booking a shortlisted flight fails unexpectedly',
      }), 'critical')
      return 'error'
    } finally {
      setBookingItemId(undefined)
      setSearchingItemId(undefined)
    }
  }, [dispatch, currency, region, showModal, intl, email, canRequestLuxPlusFlightPricing])

  return { bookFlight, bookingItemId, searchingItemId }
}

export default useBookShortlistedFlight
